import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { 
  FiX, FiUpload, FiFileText, FiVolume2, FiEye,
  FiBookOpen, FiAlertCircle, FiTrash2, FiEdit, FiDownload, FiPlus, FiExternalLink, FiMail, FiArrowLeft, FiCopy
} from 'react-icons/fi';
import apiService from '../../services/api';
import RichTextEditorTipTap from '../common/RichTextEditor';
import Notification from '../common/Notification';
import Tooltip from '../common/Tooltip';
import { formatDateForDisplay, utcToChicago, createChicagoDateTime } from '../../utils/datetime-comprehensive';
import logger from '../../utils/logger';
import styles from './AddContentModal.module.css';

// ModalOverlay removed - component now renders inline

// Reusable File Upload Field Component
const FileUploadField = ({ 
  id, 
  label, 
  optional = false, 
  accept, 
  helperText, 
  value, 
  onChange, 
  onClear, 
  error,
  maxSize,
  existingFileName,
  existingFileUrl
}) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleSelectedFile = (file, inputElement = null) => {
    if (file) {
      // Client-side validation
      if (maxSize && file.size > maxSize) {
        alert(`File size exceeds maximum allowed size of ${(maxSize / 1024 / 1024).toFixed(0)}MB`);
        if (inputElement) {
          inputElement.value = '';
        }
        return;
      }
      onChange(file);
    }
  };

  const handleFileChange = (e) => {
    handleSelectedFile(e.target.files[0], e.target);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    handleSelectedFile(file, document.getElementById(id));
  };

  const hasExistingFile = !!existingFileName && !value;

  return (
    <div className={styles.formGroup}>
      <label className={styles.label}>
        {label}
        {optional && <span className={styles.optionalLabel}> (Optional)</span>}
      </label>
      <div 
        className={`${styles.fileUploadArea} ${value ? styles.hasFile : ''} ${isDragging ? styles.dragging : ''}`}
        onClick={() => document.getElementById(id).click()}
        onDragEnter={handleDragOver}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <FiUpload size={24} />
        <div className={styles.uploadText}>
          {value ? 'Replacement file selected' : hasExistingFile ? 'Click to upload a replacement file' : 'Click to upload or drag and drop'}
        </div>
        {hasExistingFile && (
          <div className={styles.helperText}>
            A file is already uploaded. Selecting a new file will replace it.
          </div>
        )}
        {helperText && (
          <div className={styles.helperText}>
            {helperText}
          </div>
        )}
        {value && (
          <>
            <div className={styles.fileName}>{value.name}</div>
            <button
              type="button"
              className={styles.clearFileButton}
              onClick={(e) => {
                e.stopPropagation();
                onClear();
                document.getElementById(id).value = '';
              }}
            >
              Clear File
            </button>
          </>
        )}
        {!value && existingFileName && (
          <div className={styles.existingFileInfo}>
            <span className={styles.uploadedFileBadge}>
              <FiUpload size={12} />
              File uploaded
            </span>
            <span className={styles.existingFileName}>{existingFileName}</span>
            {existingFileUrl && (
              <a
                className={styles.viewExistingFileLink}
                href={existingFileUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                <FiExternalLink size={12} />
                View current file
              </a>
            )}
          </div>
        )}
        <input
          id={id}
          className={styles.fileInput}
          type="file"
          accept={accept}
          onChange={handleFileChange}
        />
      </div>
      {error && (
        <div className={styles.errorText}>{error}</div>
      )}
    </div>
  );
};

// Tabs now use CSS module - see styles.tabsContainer and styles.tab

// Form components removed - using CSS module classes instead

const ContentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  margin-top: 1.5rem;
`;

// ContentItem, ContentInfo, ContentIcon, ContentDetails, ContentTitle, ContentMeta, ContentActions removed - using CSS module resourceCard classes instead

const ActionButton = styled.button`
  background: rgba(216, 184, 137, 0.08);
  border: 1px solid rgba(216, 184, 137, 0.15);
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.625rem;
  border-radius: 0;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  min-height: 40px;
  font-family: 'Inter', sans-serif;
  
  &:hover {
    background-color: rgba(216, 184, 137, 0.15);
    color: var(--text-primary);
    border-color: var(--primary-color);
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(216, 184, 137, 0.2);
  }
  
  &.delete:hover {
    background-color: rgba(252, 129, 129, 0.1);
    color: var(--error-color);
    border-color: var(--error-color);
    box-shadow: 0 4px 12px rgba(252, 129, 129, 0.2);
  }
  
  @media (max-width: 768px) {
    min-width: 44px;
    min-height: 44px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2.5rem;
  color: var(--text-secondary);
  background: linear-gradient(135deg, rgba(216, 184, 137, 0.03), rgba(216, 184, 137, 0.06));
  border-radius: 0;
  border: 1px dashed rgba(216, 184, 137, 0.25);
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  
  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

function AddContentModal({ onClose, course, elective, onContentAdded, initialTab = 'announcements' }) {
  const [currentUser, setCurrentUser] = useState(null);
  // Ensure activeTab is always a string
  const [activeTab, setActiveTab] = useState(() => {
    const tab = initialTab || 'announcements';
    return typeof tab === 'string' ? tab : 'announcements';
  });
  const [readingsSubTab, setReadingsSubTab] = useState('new'); // 'new' or 'existing'
  const [recordingsSubTab, setRecordingsSubTab] = useState('new'); // 'new' or 'existing'
  const [announcementsSubTab, setAnnouncementsSubTab] = useState('new'); // 'new' or 'existing'
  const [syllabusSubTab, setSyllabusSubTab] = useState('new'); // 'new' or 'existing'
  const [slidesSubTab, setSlidesSubTab] = useState('new'); // 'new' or 'existing'
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ isOpen: false, type: 'success', title: '', message: '' });
  const [editingContent, setEditingContent] = useState(null);
  const [content, setContent] = useState({
    recordings: [],
    announcements: [],
    syllabus: [],
    slides: [],
    readings: []
  });
  
  // Form states for each content type
  const [formData, setFormData] = useState({
    title: '',
    description: { content_json: null, content_html: '<p></p>', content_plain: '' },
    file: null,
    url: '',
    urls: [], // For multiple URLs in readings
    priority: 'medium',
    week: '',
    duration: '',
    recordingDate: '',
    sendEmail: true // Email notification toggle
  });
  const [videoHostingType, setVideoHostingType] = useState(''); // 'external' or 'upload' or empty
  const [slidesSourceType, setSlidesSourceType] = useState('upload'); // 'upload' or 'external'

  const getDefaultSubTab = useCallback((tab, contentData) => {
    if (tab === 'notifications') return 'new';
    return (contentData?.[tab] || []).length > 0 ? 'existing' : 'new';
  }, []);

  const setSubTabForTab = useCallback((tab, value) => {
    switch (tab) {
      case 'recordings':
        setRecordingsSubTab(value);
        break;
      case 'announcements':
        setAnnouncementsSubTab(value);
        break;
      case 'syllabus':
        setSyllabusSubTab(value);
        break;
      case 'slides':
        setSlidesSubTab(value);
        break;
      case 'readings':
        setReadingsSubTab(value);
        break;
      default:
        break;
    }
  }, []);

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('faithinsight_user') || localStorage.getItem('user');
      if (storedUser) {
        setCurrentUser(JSON.parse(storedUser));
      }
    } catch (error) {
      logger.warn('Unable to parse user from localStorage', { error });
      setCurrentUser(null);
    }
  }, []);

  // Helper function to get email notification setting from localStorage
  const getEmailNotificationSetting = useCallback(() => {
    try {
      const savedSetting = localStorage.getItem('faithinsight_email_notifications');
      if (savedSetting) {
        const { enabled } = JSON.parse(savedSetting);
        return enabled;
      }
    } catch (error) {
      logger.error('Failed to load email notification setting', { error });
    }
    return formData.sendEmail; // Fallback to form data
  }, [formData.sendEmail]);

  const fetchContent = useCallback(async () => {
    try {
      const response = elective 
        ? await apiService.getElectiveContent(elective.id)
        : await apiService.getCourseContent(course.id);
      if (response.success) {
        setContent(response.content);
      }
    } catch (error) {
      logger.error('Error fetching content', { error, courseId: course?.id, electiveId: elective?.id });
      // Fallback to empty content
      setContent({
        recordings: [],
        announcements: [],
        syllabus: [],
        slides: [],
        readings: []
      });
    }
  }, [course, elective]);

  // Update activeTab when initialTab changes
  useEffect(() => {
    if (initialTab && typeof initialTab === 'string') {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  useEffect(() => {
    if (!editingContent) {
      setSubTabForTab(activeTab, getDefaultSubTab(activeTab, content));
    }
  }, [activeTab, content, editingContent, getDefaultSubTab, setSubTabForTab]);

  // Load email notification setting when notifications tab is selected
  useEffect(() => {
    if (activeTab === 'notifications') {
      try {
        const savedSetting = localStorage.getItem('faithinsight_email_notifications');
        logger.debug('Loading email notification setting for notifications tab', { savedSetting });
        if (savedSetting) {
          const { enabled } = JSON.parse(savedSetting);
          logger.debug('Setting email notification', { enabled });
          setFormData(prev => ({ ...prev, sendEmail: enabled }));
        } else {
          logger.debug('No saved email notification setting, using default', { default: true });
        }
      } catch (error) {
        logger.error('Failed to load email notification setting', { error });
      }
    }
  }, [activeTab]);

  useEffect(() => {
    if (course || elective) {
      fetchContent();
      
      // Load email notification setting
      try {
        const savedSetting = localStorage.getItem('faithinsight_email_notifications');
        logger.debug('Loading email notification setting', { savedSetting });
        if (savedSetting) {
          const { enabled } = JSON.parse(savedSetting);
          logger.debug('Setting email notification', { enabled });
          setFormData(prev => ({ ...prev, sendEmail: enabled }));
        } else {
          logger.debug('No saved email notification setting, using default', { default: true });
        }
      } catch (error) {
        logger.error('Failed to load email notification setting', { error });
        // Keep default value (true)
      }
    }
  }, [course, elective, fetchContent]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'description') {
      // Handle description as an object with content properties
      setFormData(prev => ({ 
        ...prev, 
        [name]: { 
          content_json: null, 
          content_html: `<p>${value}</p>`, 
          content_plain: value 
        } 
      }));
    } else if (name === 'url' && activeTab === 'recordings' && value.trim()) {
      // Clear file when URL is entered for recordings
      setFormData(prev => ({ 
        ...prev, 
        [name]: value,
        file: null
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Handle multiple URLs for readings
  const addUrl = () => {
    setFormData(prev => ({
      ...prev,
      urls: [...(prev.urls || []), { id: Date.now(), url: '', label: '' }]
    }));
  };

  const removeUrl = (id) => {
    setFormData(prev => ({
      ...prev,
      urls: (prev.urls || []).filter(url => url.id !== id)
    }));
  };

  const updateUrl = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      urls: (prev.urls || []).map(url => 
        url.id === id ? { ...url, [field]: value } : url
      )
    }));
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSubTabForTab(tab, getDefaultSubTab(tab, content));
    resetForm(); // Reset form and editing state when switching tabs
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: { content_json: null, content_html: '<p></p>', content_plain: '' },
      file: null,
      url: '',
      urls: [],
      priority: 'medium',
      week: '',
      duration: '',
      recordingDate: '',
      sendEmail: true
    });
    setEditingContent(null);
    setVideoHostingType('');
    setSlidesSourceType('upload');
  };

  const isLocalFileUrl = (url) => typeof url === 'string' && /^\s*file:\/\//i.test(url);

  const hasLocalFileUrl = () => {
    if (isLocalFileUrl(formData.url)) {
      return true;
    }

    return Array.isArray(formData.urls) && formData.urls.some(urlItem => isLocalFileUrl(urlItem.url));
  };

  const getExistingFileName = (contentItem = editingContent) => (
    contentItem?.file_name || (contentItem?.file_path ? contentItem.file_path.split('/').pop() : '')
  );

  const getExistingFileUrl = (contentItem = editingContent) => {
    const filePath = contentItem?.file_path || contentItem?.file_name;
    return filePath ? `${apiService.baseURL}/course-files/${encodeURIComponent(filePath.split('/').pop())}` : '';
  };

  const getDownloadUrl = (url, fileName = 'recording') => {
    if (!url) return '';

    try {
      const downloadUrl = new URL(url, window.location.origin);
      downloadUrl.searchParams.set('response-content-disposition', `attachment; filename="${fileName}"`);
      return downloadUrl.toString();
    } catch (error) {
      return url;
    }
  };

  const isViewableFile = (fileNameOrPath) => {
    if (!fileNameOrPath || typeof fileNameOrPath !== 'string') {
      return false;
    }

    const fileName = fileNameOrPath.split('/').pop().split('?')[0].split('#')[0];
    const extension = fileName.toLowerCase().split('.').pop();
    return extension === 'pdf' || extension === 'html' || extension === 'htm';
  };

  const copyTextToClipboard = async (text, successMessage = 'Link copied to clipboard.') => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      setNotification({
        isOpen: true,
        type: 'success',
        title: 'Copied',
        message: successMessage
      });
    } catch (error) {
      logger.error('Failed to copy text to clipboard', { error });
      setNotification({
        isOpen: true,
        type: 'error',
        title: 'Copy failed',
        message: 'Failed to copy link. Please copy manually.'
      });
    }
  };

  // Helper function to safely get singular form of tab name
  const getSingularTabName = (tab) => {
    const safeTab = typeof tab === 'string' ? tab : 'recordings';
    
    // Map frontend tab names to database content types
    const tabMapping = {
      'recordings': 'recording',
      'announcements': 'announcement', 
      'syllabus': 'syllabus',
      'slides': 'slide',
      'readings': 'reading'
    };
    
    return tabMapping[safeTab] || safeTab.slice(0, -1);
  };

  const handleNotificationsSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      logger.debug('Current formData.sendEmail', { sendEmail: formData.sendEmail });
      
      // Save email notification setting to localStorage
      // In a real implementation, this would be saved to user preferences in the database
      const settingData = {
        enabled: formData.sendEmail,
        updatedAt: new Date().toISOString()
      };
      
      localStorage.setItem('faithinsight_email_notifications', JSON.stringify(settingData));
      logger.debug('Email notification setting saved', { settingData });
      
      alert(`Email notification setting saved successfully! Notifications are now ${formData.sendEmail ? 'ENABLED' : 'DISABLED'}.`);
      
      setLoading(false);
      onClose(); // Close the modal after saving
    } catch (error) {
      logger.error('Failed to save notification settings', { error });
      alert('Failed to save notification settings. Please try again.');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    logger.debug('Form submission started', { formData, urls: formData.urls, activeTab });

    // Validate title is not empty
    if (!formData.title || !formData.title.trim()) {
      alert('Please enter a title.');
      return;
    }

    if (hasLocalFileUrl()) {
      alert('Local file URLs are not allowed. Please upload the file or use a public https:// link.');
      return;
    }

    // If we're editing existing content, use the update handler
    if (editingContent) {
      return handleUpdateContent(e);
    }
    
    setLoading(true);

    try {
      // Validate recordings: must select a hosting type and provide required data
      if (activeTab === 'recordings') {
        if (!videoHostingType) {
          alert('Please select a video hosting type (External or Upload).');
          setLoading(false);
          return;
        }
        
        if (videoHostingType === 'external') {
          const hasUrl = formData.url && formData.url.trim();
          if (!hasUrl) {
            alert('Please provide a Video URL.');
            setLoading(false);
            return;
          }
        } else if (videoHostingType === 'upload') {
          const hasFile = formData.file;
          if (!hasFile) {
            alert('Please upload an MP4 file.');
            setLoading(false);
            return;
          }
        }
      }

      if (activeTab === 'slides') {
        const hasUploadedFile = formData.file || editingContent?.file_path || editingContent?.file_name;
        const hasUrl = formData.url && formData.url.trim();

        if (slidesSourceType === 'upload' && !hasUploadedFile) {
          alert('Please upload a document file.');
          setLoading(false);
          return;
        }

        if (slidesSourceType === 'external' && !hasUrl) {
          alert('Please provide a link URL.');
          setLoading(false);
          return;
        }
      }

      if (activeTab === 'slides') {
        const hasUploadedFile = formData.file || editingContent?.file_path || editingContent?.file_name;
        const hasUrl = formData.url && formData.url.trim();

        if (slidesSourceType === 'upload' && !hasUploadedFile) {
          alert('Please upload a document file.');
          setLoading(false);
          return;
        }

        if (slidesSourceType === 'external' && !hasUrl) {
          alert('Please provide a link URL.');
          setLoading(false);
          return;
        }
      }

      // Use FormData if there's a file upload, otherwise use regular object
      let contentData;
      
      if (formData.file) {
        // Use FormData for file uploads
        contentData = new FormData();
        contentData.append('type', getSingularTabName(activeTab));
        contentData.append('title', formData.title);
        contentData.append('priority', formData.priority);
        
        if (formData.description && (typeof formData.description === 'object' ? formData.description.content_html?.trim() : formData.description.trim())) {
          const descriptionHtml = typeof formData.description === 'object' ? formData.description.content_html : formData.description;
          contentData.append('description', descriptionHtml);
        }
        
        if (activeTab === 'recordings') {
          if (formData.week) contentData.append('week', formData.week);
          if (formData.duration && formData.duration.trim()) contentData.append('duration', formData.duration);
          if (formData.recordingDate && formData.recordingDate.trim()) {
            const chicagoMidnight = createChicagoDateTime(formData.recordingDate, '00:00');
            if (chicagoMidnight) {
              const utcIso = chicagoMidnight.toUTC().toISO();
              if (utcIso) {
                contentData.append('recordingDate', utcIso);
              }
            }
          }
        }
        
        if (activeTab === 'syllabus') {
          // Syllabus is course-wide, no week needed
        }
        
        if (activeTab === 'slides') {
          if (formData.week) contentData.append('week', formData.week);
        }
        
        if (activeTab === 'readings') {
          if (formData.week) contentData.append('week', formData.week);
          // Handle multiple URLs for readings
          if (formData.urls && Array.isArray(formData.urls) && formData.urls.length > 0) {
            const validUrls = formData.urls.filter(urlItem => urlItem.url && urlItem.url.trim());
            if (validUrls.length > 0) {
              contentData.append('urls', JSON.stringify(validUrls));
            }
          }
        }
        
        if (formData.url && formData.url.trim()) {
          contentData.append('url', formData.url);
        }

        if (activeTab === 'slides' && slidesSourceType === 'upload') {
          contentData.delete('url');
        }

        contentData.append('sendEmail', getEmailNotificationSetting());
        contentData.append('file', formData.file);
      } else {
        // Use regular object for non-file uploads
        contentData = {
          type: getSingularTabName(activeTab),
          title: formData.title,
          priority: formData.priority
        };

        if (formData.description && (typeof formData.description === 'object' ? formData.description.content_html?.trim() : formData.description.trim())) {
          const descriptionHtml = typeof formData.description === 'object' ? formData.description.content_html : formData.description;
          contentData.description = descriptionHtml;
        }

        if (activeTab === 'recordings') {
          if (formData.week) contentData.week = formData.week;
          if (formData.duration && formData.duration.trim()) contentData.duration = formData.duration;
          // Always include recordingDate for recordings, even if null
          if (formData.recordingDate && formData.recordingDate.trim()) {
            const chicagoMidnight = createChicagoDateTime(formData.recordingDate, '00:00');
            contentData.recordingDate = chicagoMidnight ? chicagoMidnight.toUTC().toISO() : null;
          } else {
            contentData.recordingDate = null;
          }
        }

        if (activeTab === 'syllabus') {
          // Syllabus is course-wide, no week needed
        }

        if (activeTab === 'slides') {
          if (formData.week) contentData.week = formData.week;
        }

        if (activeTab === 'readings') {
          if (formData.week) contentData.week = formData.week;
          // Handle multiple URLs for readings
          if (formData.urls && Array.isArray(formData.urls) && formData.urls.length > 0) {
            const validUrls = formData.urls.filter(urlItem => urlItem.url && urlItem.url.trim());
            if (validUrls.length > 0) {
              contentData.urls = validUrls;
            }
          }
        }

        if (formData.url && formData.url.trim()) {
          contentData.url = formData.url;
        }

        if (activeTab === 'slides' && slidesSourceType === 'upload') {
          delete contentData.url;
        }

        contentData.sendEmail = getEmailNotificationSetting();
      }

      const response = elective
        ? await apiService.addElectiveContent(elective.id, contentData)
        : await apiService.addCourseContent(course.id, contentData);
      
      if (response.success) {
        resetForm();
        fetchContent(); // Refresh content list
        onContentAdded && onContentAdded('added');
        onClose();
      } else {
        throw new Error(response.message || 'Failed to add content');
      }
    } catch (error) {
      logger.error('Error adding content', { error, courseId: course?.id, electiveId: elective?.id, activeTab });
      alert('Failed to add content: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditContent = (contentItem) => {
    
    setEditingContent(contentItem);
    
    // Keep the current tab (existing) when editing - form will show regardless
    // The form is shown when editingContent is set, regardless of sub-tab
    
    // Parse existing URLs if they exist
    let existingUrls = [];
    if (contentItem.urls) {
      try {
        // If urls is a string (JSON), parse it
        if (typeof contentItem.urls === 'string') {
          existingUrls = JSON.parse(contentItem.urls);
        } else if (Array.isArray(contentItem.urls)) {
          // If urls is already an array, use it directly
          existingUrls = contentItem.urls;
        }
        
        // Ensure each URL has an id field for React keys
        existingUrls = existingUrls.map((urlItem, index) => ({
          ...urlItem,
          id: urlItem.id || `existing-${index}-${Date.now()}`
        }));
        
      } catch (error) {
        logger.error('Error parsing URLs', { error, description: contentItem.description });
        existingUrls = [];
      }
    }
    
    // URLs are now stored separately in the urls field, no need to extract from description
    
    const chicagoDate = contentItem.recording_date ? (function() {
      try {
        // Handle the recording date conversion properly
        // The database stores dates as UTC timestamps, but for date-only fields like recording_date,
        // we should extract the date part directly from the ISO string to match how it's displayed
        // This matches the logic in VideoPlaylist.js and VideoPlayer.js which extract the date part directly
        
        let isoString;
        if (contentItem.recording_date instanceof Date) {
          isoString = contentItem.recording_date.toISOString();
        } else if (typeof contentItem.recording_date === 'string') {
          isoString = contentItem.recording_date;
        } else {
          logger.error('Unexpected recording_date type', { type: typeof contentItem.recording_date, value: contentItem.recording_date });
          return '';
        }
        
        // Extract the date part directly from the ISO string (YYYY-MM-DD)
        // This matches how the date is displayed in VideoPlaylist and VideoPlayer components
        // e.g., "2026-01-07T00:00:00.000Z" -> "2026-01-07"
        const datePart = isoString.slice(0, 10);
        if (datePart && datePart.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return datePart;
        }
        
        // Fallback: if date extraction fails, try converting to Chicago timezone
        const chicagoDateTime = utcToChicago(isoString);
        if (chicagoDateTime.isValid) {
          return chicagoDateTime.toISODate();
        }
        
        logger.error('Invalid date format', { isoString });
        return '';
      } catch (e) {
        logger.error('Error converting recording date', { error: e, recording_date: contentItem.recording_date });
        return '';
      }
    })() : '';

    const newFormData = {
      title: contentItem.title || '',
      description: contentItem.description ? {
        content_json: null,
        content_html: contentItem.description,
        content_plain: contentItem.description.replace(/<[^>]*>/g, '').trim()
      } : { content_json: null, content_html: '', content_plain: '' },
      file: null,
      url: contentItem.url || '',
      urls: existingUrls, // Use existing URLs instead of empty array
      priority: contentItem.priority || 'medium',
      week: contentItem.week || '',
      duration: contentItem.duration || '',
      recordingDate: chicagoDate
    };
    
    // Set video hosting type based on what exists
    if (contentItem.url && contentItem.url.trim()) {
      setVideoHostingType('external');
    } else {
      setVideoHostingType('upload');
    }

    if (contentItem.type === 'slide') {
      setSlidesSourceType(contentItem.file_path || contentItem.file_name ? 'upload' : 'external');
    }
    
    setFormData(newFormData);
  };

  const handleUpdateContent = async (e) => {
    e.preventDefault();
    if (!editingContent) return;


    setLoading(true);
    try {
      // Validate title
      if (!formData.title || !formData.title.trim()) {
        alert('Please enter a title.');
        setLoading(false);
        return;
      }

      if (hasLocalFileUrl()) {
        alert('Local file URLs are not allowed. Please upload the file or use a public https:// link.');
        setLoading(false);
        return;
      }

      // Validate recordings: must have either URL or file (existing or new)
      if (activeTab === 'recordings') {
        if (!videoHostingType) {
          alert('Please select a video hosting type (External or Upload).');
          setLoading(false);
          return;
        }

        if (videoHostingType === 'external') {
          const hasUrl = formData.url && formData.url.trim();
          if (!hasUrl) {
            alert('Please provide a Video URL.');
            setLoading(false);
            return;
          }
        } else if (videoHostingType === 'upload') {
          // Allow update if there's a new file OR if existing content already has a file
          const hasNewFile = formData.file;
          const hasExistingFile = editingContent.file_path;
          if (!hasNewFile && !hasExistingFile) {
            alert('Please upload an MP4 file.');
            setLoading(false);
            return;
          }
        }
      }

      // Extract description HTML - handle both TipTap format and legacy format
      let descriptionHtml = '';
      if (typeof formData.description === 'object') {
        descriptionHtml = formData.description.content_html || formData.description.html || '';
      } else if (typeof formData.description === 'string') {
        descriptionHtml = formData.description;
      }

      const contentData = {
        title: formData.title,
        priority: formData.priority,
        description: descriptionHtml,
        url: activeTab === 'slides' && slidesSourceType === 'upload' ? '' : formData.url || '',
        sendEmail: getEmailNotificationSetting()
      };


      // Include all fields for recordings, even if empty (to allow clearing)
      if (activeTab === 'recordings') {
        // Always include week field to allow clearing (null) or setting a value
        if (formData.week && String(formData.week).trim() !== '') {
          contentData.week = parseInt(formData.week, 10);
        } else {
          contentData.week = null; // Explicitly set to null to clear the field
        }
        // Always include duration field to allow clearing
        if (formData.duration && formData.duration.trim() !== '') {
          contentData.duration = formData.duration;
        } else {
          contentData.duration = null; // Explicitly set to null to clear the field
        }
        if (formData.recordingDate) {
          const chicagoMidnight = createChicagoDateTime(formData.recordingDate, '00:00');
          // Force exact UTC midnight equivalence for the chosen wall date to avoid -1 day shifts when parsing elsewhere
          const utcIso = chicagoMidnight ? chicagoMidnight.toUTC().startOf('day').toISO() : null;
          contentData.recordingDate = utcIso;
        } else {
          contentData.recordingDate = null; // Explicitly set to null to clear the field
        }
      }

      if (activeTab === 'slides') {
        if (formData.week && String(formData.week).trim() !== '') {
          contentData.week = parseInt(formData.week, 10);
        } else {
          contentData.week = null;
        }
      }

      // Include URLs for readings
      if (activeTab === 'readings') {
        // Always include week field to allow clearing (null) or setting a value
        if (formData.week && String(formData.week).trim() !== '') {
          contentData.week = parseInt(formData.week, 10);
        } else {
          contentData.week = null; // Explicitly set to null to clear the field
        }
        // Handle multiple URLs for readings - always include URLs field to allow clearing
        if (formData.urls && Array.isArray(formData.urls) && formData.urls.length > 0) {
          const validUrls = formData.urls.filter(urlItem => urlItem.url && urlItem.url.trim());
          contentData.urls = validUrls; // Always set URLs, even if empty array
        } else {
          contentData.urls = []; // Explicitly set empty array to clear existing URLs
        }
      }


      const response = await apiService.updateCourseContent(editingContent.id, contentData);
      
      if (response.success) {
        resetForm();
        setEditingContent(null);
        fetchContent(); // Refresh content list
        onContentAdded && onContentAdded('updated');
      } else {
        throw new Error(response.message || 'Failed to update content');
      }
    } catch (error) {
      logger.error('Error updating content', { error, contentId: editingContent?.id, activeTab });
      alert('Failed to update content: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteContent = async (contentId) => {
    if (!window.confirm('Are you sure you want to delete this content?')) {
      return;
    }

    try {
      const response = await apiService.deleteCourseContent(contentId);
      if (response.success) {
        fetchContent(); // Refresh content list
      } else {
        throw new Error(response.message || 'Failed to delete content');
      }
    } catch (error) {
      logger.error('Error deleting content', { error, contentId });
      alert('Failed to delete content: ' + error.message);
    }
  };

  const renderForm = () => {
    const safeTab = typeof activeTab === 'string' ? activeTab : 'recordings';
    const currentContent = content[safeTab] || [];
    
    // For all content types, show different content based on sub-tab
    const getCurrentSubTab = () => {
      switch (activeTab) {
        case 'recordings': return recordingsSubTab;
        case 'announcements': return announcementsSubTab;
        case 'syllabus': return syllabusSubTab;
        case 'slides': return slidesSubTab;
        case 'readings': return readingsSubTab;
        default: return 'new';
      }
    };

    const currentSubTab = getCurrentSubTab();
    
    // Show existing items list only if NOT editing and on existing tab
    if (currentSubTab === 'existing' && !editingContent) {
      return (
        <div>
          <ContentList>
            {currentContent.length > 0 ? (
            currentContent.map((item, index) => {
              // Get icon based on content type
              const getIcon = () => {
                switch (activeTab) {
                  case 'recordings': return <FiVolume2 size={20} />;
                  case 'announcements': return <FiAlertCircle size={20} />;
                  case 'syllabus': return <FiBookOpen size={20} />;
                  case 'slides': return <FiFileText size={20} />;
                  case 'readings': return <FiBookOpen size={20} />;
                  default: return null;
                }
              };

              // Build title with week prefix if applicable
              const getTitle = () => {
                const weekPrefix = (activeTab === 'recordings' || activeTab === 'slides' || activeTab === 'readings') && item.week 
                  ? `Week ${item.week}: ` 
                  : '';
                return `${weekPrefix}${item.title}`;
              };

              // Build secondary meta (author + date)
              const getSecondaryMeta = () => {
                const parts = [];
                if (item.creator_first_name && item.creator_last_name) {
                  parts.push(`${item.creator_first_name} ${item.creator_last_name}`);
                }
                parts.push(`Posted: ${formatDateForDisplay(item.created_at, 'M/d/yyyy h:mm a')}`);
                return parts.join(' • ');
              };

              const uploadedFileName = item.file_name || (item.file_path ? item.file_path.split('/').pop() : 'File');
              const existingFileUrl = getExistingFileUrl(item);
              const externalRecordingUrl = activeTab === 'recordings' && typeof item.url === 'string' ? item.url.trim() : '';

              // Build file details
              const getFileDetails = () => {
                const parts = [];
                if (activeTab === 'recordings' && item.recording_date) {
                  parts.push(`Recorded: ${formatDateForDisplay(item.recording_date, 'M/d/yyyy')}`);
                }
                if (activeTab === 'recordings' && item.duration) {
                  parts.push(`Duration: ${item.duration}`);
                }
                // Show file info if file_name or file_path exists
                if (item.file_name || item.file_path) {
                  parts.push(`${activeTab === 'slides' ? 'Uploaded file' : 'File'}: ${uploadedFileName}`);
                }
                if (item.file_size) {
                  parts.push(`Size: ${(item.file_size / 1024 / 1024).toFixed(2)} MB`);
                }
                return parts.length > 0 ? parts.join(' • ') : null;
              };

              return (
                <div key={index} className={styles.resourceCard}>
                  <div className={styles.resourceCardContent}>
                    {/* Zone 1: Icon + Title */}
                    <div className={styles.resourceCardHeader}>
                      <div className={styles.resourceCardIcon}>
                        {getIcon()}
                      </div>
                      <h3 className={styles.resourceCardTitle}>
                        {getTitle()}
                      </h3>
                    </div>

                    {/* Zone 2: Secondary Meta (Author + Date) */}
                    <div className={styles.resourceCardMeta}>
                      {getSecondaryMeta()}
                    </div>

                    {/* Zone 3: File Details (if exists) */}
                    {getFileDetails() && (
                      <div className={styles.resourceCardFileDetails}>
                        {activeTab === 'slides' && (item.file_name || item.file_path) && (
                          <span className={styles.uploadedFileBadge}>
                            <FiUpload size={12} />
                            File uploaded
                          </span>
                        )}
                        {getFileDetails()}
                      </div>
                    )}

                    {externalRecordingUrl && (
                      <div className={styles.resourceCardLinkDetails}>
                        <a
                          className={styles.externalLinkBadge}
                          href={externalRecordingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FiExternalLink size={12} />
                          Open external video link
                        </a>
                        <button
                          type="button"
                          className={styles.copyDirectVideoLinkButton}
                          title="Copy direct video link"
                          aria-label="Copy direct video link"
                          onClick={() => copyTextToClipboard(externalRecordingUrl, 'Recording link copied to clipboard.')}
                        >
                          <FiCopy size={13} />
                        </button>
                      </div>
                    )}

                    {/* Special handling for readings with URLs */}
                    {activeTab === 'readings' && item.urls && item.urls.length > 0 && (
                      <div style={{ marginTop: '12px', marginLeft: '32px' }}>
                        <div style={{ 
                          display: 'flex', 
                          gap: '8px', 
                          flexWrap: 'wrap' 
                        }}>
                          {item.urls.map((urlItem, urlIndex) => (
                            <a
                              key={urlIndex}
                              href={urlItem.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ 
                                fontSize: '12px', 
                                padding: '6px 12px',
                                color: 'var(--text-muted)',
                                textDecoration: 'none',
                                border: '1px solid rgba(15, 23, 42, 0.1)',
                                borderRadius: '6px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                transition: 'all 0.2s ease',
                                fontFamily: 'Inter, sans-serif'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = 'var(--primary-color)';
                                e.currentTarget.style.color = 'var(--primary-color)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = 'rgba(15, 23, 42, 0.1)';
                                e.currentTarget.style.color = 'var(--text-muted)';
                              }}
                            >
                              <FiExternalLink size={12} />
                              {urlItem.label || `Link ${urlIndex + 1}`}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions: View/Download (primary), Edit (secondary), Delete (danger) */}
                  <div className={styles.resourceCardActions}>
                    {((activeTab === 'slides' && existingFileUrl && isViewableFile(uploadedFileName)) || externalRecordingUrl) && (
                      <Tooltip content="View" position="bottom" className={styles.resourceCardActionTooltip}>
                        <a
                          className={`${styles.resourceCardActionButton} ${styles.primary}`}
                          title="View"
                          href={externalRecordingUrl || existingFileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="View"
                        >
                          <FiEye size={16} />
                        </a>
                      </Tooltip>
                    )}
                    {/* Download - Primary, always visible if file exists */}
                    {((activeTab === 'recordings' && (externalRecordingUrl || item.file_path || item.file_name)) || ((activeTab === 'syllabus' || activeTab === 'slides' || activeTab === 'readings') && (item.file_path || item.file_name))) && (
                      <Tooltip content="Download" position="bottom" className={styles.resourceCardActionTooltip}>
                        <button
                          className={`${styles.resourceCardActionButton} ${styles.primary}`}
                          title="Download"
                          onClick={() => {
                            const downloadUrl = existingFileUrl || externalRecordingUrl;
                            if (downloadUrl) {
                              window.open(activeTab === 'recordings' ? getDownloadUrl(downloadUrl, uploadedFileName) : downloadUrl, '_blank');
                            }
                          }}
                        >
                          <FiDownload size={16} />
                        </button>
                      </Tooltip>
                    )}
                    {/* Edit - Secondary, appears on hover (desktop) */}
                    <Tooltip content="Edit" position="bottom" className={styles.resourceCardActionTooltip}>
                      <button
                        className={`${styles.resourceCardActionButton} ${styles.secondary}`}
                        title="Edit"
                        onClick={() => handleEditContent(item)}
                      >
                        <FiEdit size={16} />
                      </button>
                    </Tooltip>
                    {/* Delete - Danger, separated slightly, appears on hover (desktop) */}
                    <Tooltip content="Delete" position="bottom" className={styles.resourceCardActionTooltip}>
                      <button
                        className={`${styles.resourceCardActionButton} ${styles.delete}`}
                        title="Delete"
                        onClick={() => handleDeleteContent(item.id)}
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </Tooltip>
                  </div>
                </div>
              );
            })
          ) : (
            <EmptyState>
              No {safeTab} available yet. Add your first {getSingularTabName(activeTab)} below.
            </EmptyState>
          )}
        </ContentList>
        </div>
      );
    }
    
    // For readings new tab or other tabs, show the form
    return (
      <>
        {/* Show form for new sub-tab OR when editing existing content, but not for notifications tab */}
        {(currentSubTab === 'new' || editingContent) && activeTab !== 'notifications' && (
          <form onSubmit={editingContent ? handleUpdateContent : handleSubmit}>
            {/* Basics Section */}
            <div className={styles.sectionCard}>
              <div className={styles.sectionHeader}>
                <span className={styles.sectionHeaderText}>Basics</span>
              </div>
              <div className={styles.formGroup}>
                <label className={`${styles.label} ${styles.labelRequired}`}>Title</label>
                <input
                  className={styles.input}
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder={`Enter ${getSingularTabName(activeTab)} title`}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Description</label>
                {(activeTab === 'announcements' || activeTab === 'readings') ? (
                  <RichTextEditorTipTap
                    value={formData.description}
                    onChange={(value) => {
                      setFormData(prev => ({ ...prev, description: value }));
                    }}
                    placeholder={`Enter ${getSingularTabName(activeTab)} description`}
                  />
                ) : (
                  <textarea
                    className={styles.textarea}
                    name="description"
                    value={typeof formData.description === 'object' ? formData.description.content_plain || '' : formData.description || ''}
                    onChange={handleInputChange}
                    placeholder={`Enter ${getSingularTabName(activeTab)} description`}
                  />
                )}
              </div>
            </div>

          {activeTab === 'recordings' && (
            <>
              {/* Scheduling Section */}
              <div className={styles.sectionCard}>
                <div className={styles.sectionHeader}>
                  <span className={styles.sectionHeaderText}>Scheduling</span>
                </div>
                <div className={styles.formGrid}>
                  <div className={styles.formGroup}>
                    <label className={`${styles.label} ${styles.labelRequired}`}>Week Number</label>
                    <input
                      className={styles.input}
                      name="week"
                      type="number"
                      min="1"
                      max="52"
                      value={formData.week}
                      onChange={handleInputChange}
                      placeholder="Enter week number"
                      required
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>Recording Date</label>
                    <input
                      className={styles.input}
                      name="recordingDate"
                      type="date"
                      value={formData.recordingDate}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFormData(prev => ({ ...prev, recordingDate: v }));
                      }}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>Duration</label>
                    <input
                      className={styles.input}
                      name="duration"
                      value={formData.duration}
                      onChange={handleInputChange}
                      placeholder="e.g., 1:30:45 or 90 minutes"
                    />
                  </div>
                </div>
              </div>

              {/* Media Section */}
              <div className={styles.sectionCard}>
                <div className={styles.sectionHeader}>
                  <span className={styles.sectionHeaderText}>Media</span>
                </div>
                <div className={styles.formGroup}>
                  <label className={`${styles.label} ${styles.labelRequired}`}>Video Hosting</label>
                  <div className={styles.radioGroup}>
                    <label className={`${styles.radioPill} ${videoHostingType === 'external' ? styles.active : ''}`}>
                      <input
                        type="radio"
                        name="videoHostingType"
                        value="external"
                        checked={videoHostingType === 'external'}
                        onChange={(e) => {
                          setVideoHostingType('external');
                          setFormData(prev => ({ ...prev, file: null }));
                          const fileInput = document.getElementById('recordings-file-input');
                          if (fileInput) fileInput.value = '';
                        }}
                      />
                      External
                    </label>
                    <label className={`${styles.radioPill} ${videoHostingType === 'upload' ? styles.active : ''}`}>
                      <input
                        type="radio"
                        name="videoHostingType"
                        value="upload"
                        checked={videoHostingType === 'upload'}
                        onChange={(e) => {
                          setVideoHostingType('upload');
                          setFormData(prev => ({ ...prev, url: '' }));
                        }}
                      />
                      Upload
                    </label>
                  </div>
                </div>

                {videoHostingType === 'external' && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      Video URL
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            url: 'https://d8eiitau7n3ii.cloudfront.net/'
                          }));
                        }}
                        style={{
                          marginLeft: '0.5rem',
                          padding: '0.25rem 0.5rem',
                          fontSize: '11px',
                          fontWeight: 600,
                          color: 'var(--primary-color)',
                          backgroundColor: 'rgba(216, 184, 137, 0.1)',
                          border: '1px solid rgba(216, 184, 137, 0.2)',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        Paste Host
                      </button>
                    </label>
                    <input
                      className={styles.input}
                      name="url"
                      type="url"
                      value={formData.url}
                      onChange={handleInputChange}
                      placeholder="https://your-server.com/videos/week1.mp4"
                    />
                  </div>
                )}

                {videoHostingType === 'upload' && (
                  <FileUploadField
                    id="recordings-file-input"
                    label="MP4 File Upload"
                    optional={false}
                    accept=".mp4,video/mp4"
                    helperText="Accepted format: MP4 (max 3GB)"
                    value={formData.file}
                    onChange={(file) => setFormData(prev => ({ ...prev, file }))}
                    onClear={() => setFormData(prev => ({ ...prev, file: null }))}
                    maxSize={3 * 1024 * 1024 * 1024} // 3GB
                    existingFileName={getExistingFileName()}
                    existingFileUrl={getExistingFileUrl()}
                  />
                )}
              </div>
            </>
          )}

          {activeTab === 'slides' && (
            <>
              <div className={styles.sectionCard}>
                <div className={styles.sectionHeader}>
                  <span className={styles.sectionHeaderText}>Slide Source</span>
                </div>
                <div className={styles.formGroup}>
                  <label className={`${styles.label} ${styles.labelRequired}`}>Choose One</label>
                  <div className={styles.radioGroup}>
                    <label className={`${styles.radioPill} ${slidesSourceType === 'upload' ? styles.active : ''}`}>
                      <input
                        type="radio"
                        name="slidesSourceType"
                        value="upload"
                        checked={slidesSourceType === 'upload'}
                        onChange={() => {
                          setSlidesSourceType('upload');
                          setFormData(prev => ({ ...prev, url: '' }));
                        }}
                      />
                      Upload File
                    </label>
                    <label className={`${styles.radioPill} ${slidesSourceType === 'external' ? styles.active : ''}`}>
                      <input
                        type="radio"
                        name="slidesSourceType"
                        value="external"
                        checked={slidesSourceType === 'external'}
                        onChange={() => {
                          setSlidesSourceType('external');
                          setFormData(prev => ({ ...prev, file: null }));
                          const fileInput = document.getElementById('file-input');
                          if (fileInput) fileInput.value = '';
                        }}
                      />
                      External Link
                    </label>
                  </div>
                </div>

                {slidesSourceType === 'upload' && (
                  <FileUploadField
                    id="file-input"
                    label="Document File"
                    optional={false}
                    accept=".pdf,.html,.htm,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                    helperText="Accepted formats: PDF, HTML, DOC, DOCX, XLS, XLSX, PPT, PPTX"
                    value={formData.file}
                    onChange={(file) => setFormData(prev => ({ ...prev, file }))}
                    onClear={() => setFormData(prev => ({ ...prev, file: null }))}
                    existingFileName={getExistingFileName()}
                    existingFileUrl={getExistingFileUrl()}
                  />
                )}

                {slidesSourceType === 'external' && (
                  <div className={styles.formGroup}>
                    <label className={`${styles.label} ${styles.labelRequired}`}>Link URL</label>
                    <input
                      className={styles.input}
                      name="url"
                      type="url"
                      value={formData.url}
                      onChange={handleInputChange}
                      placeholder=""
                    />
                  </div>
                )}
              </div>

              {/* Week Section */}
              <div className={styles.sectionCard}>
                <div className={styles.sectionHeader}>
                  <span className={styles.sectionHeaderText}>Week</span>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Week (Optional)</label>
                  <input
                    className={styles.input}
                    name="week"
                    type="number"
                    min="1"
                    max="52"
                    value={formData.week}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </>
          )}

          {activeTab === 'readings' && (
            <>
              {/* Reading URLs Section */}
              <div className={styles.sectionCard}>
                <div className={styles.sectionHeader}>
                  <span className={styles.sectionHeaderText}>Reading URLs</span>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Reading URLs (Optional)</label>
                  <div style={{ 
                    marginBottom: '1rem',
                    padding: '1rem',
                    border: '1px solid rgba(15, 23, 42, 0.1)',
                    borderRadius: '12px',
                    backgroundColor: 'white'
                  }}>
                  {(formData.urls || []).map((urlItem, index) => (
                    <div key={urlItem.id} style={{ 
                      position: 'relative',
                      marginBottom: '1rem',
                      padding: '1rem',
                      border: '1px solid rgba(15, 23, 42, 0.12)',
                      borderRadius: '12px',
                      backgroundColor: 'white'
                    }}>
                      <ActionButton
                        type="button"
                        className="danger"
                        size="sm"
                        onClick={() => removeUrl(urlItem.id)}
                        style={{ 
                          position: 'absolute',
                          top: '0.5rem',
                          right: '0.5rem',
                          padding: '0.5rem',
                          minWidth: '32px',
                          minHeight: '32px'
                        }}
                      >
                        <FiX size={14} />
                      </ActionButton>
                      <div style={{ 
                        paddingRight: '2.5rem' // Make space for the X button
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          marginBottom: '0.75rem',
                          gap: '0.5rem'
                        }}>
                          <div style={{
                            backgroundColor: 'var(--primary-color)',
                            color: 'var(--text-inverse)',
                            borderRadius: 0,
                            width: '24px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            flexShrink: 0
                          }}>
                            {index + 1}
                          </div>
                          <label style={{ 
                            fontSize: '0.75rem', 
                            fontWeight: '600', 
                            color: 'var(--text-secondary)',
                            margin: 0
                          }}>
                            Link Label
                          </label>
                        </div>
                        <div style={{ 
                          display: 'flex', 
                          gap: '0.5rem',
                          marginBottom: '0.75rem'
                        }}>
                          <div style={{
                            width: '24px',
                            height: '40px', // Match input height
                            flexShrink: 0
                          }}>
                            {/* Invisible spacer to align with the numbered badge above */}
                          </div>
                          <input
                            className={styles.input}
                            type="text"
                            placeholder="e.g., 'Main Article', 'PDF Download'"
                            value={urlItem.label}
                            onChange={(e) => updateUrl(urlItem.id, 'label', e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                              }
                              // Prevent any key from bubbling up and potentially closing the modal
                              e.stopPropagation();
                            }}
                            style={{ 
                              flex: 1
                            }}
                          />
                        </div>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          marginBottom: '0.25rem',
                          gap: '0.5rem'
                        }}>
                          <div style={{
                            width: '24px',
                            height: '24px',
                            flexShrink: 0
                          }}>
                            {/* Invisible spacer to align with the numbered badge above */}
                          </div>
                          <label style={{ 
                            fontSize: '0.75rem', 
                            fontWeight: '600', 
                            color: 'var(--text-secondary)',
                            margin: 0
                          }}>
                            Link URL
                          </label>
                        </div>
                        <div style={{ 
                          display: 'flex', 
                          gap: '0.5rem'
                        }}>
                          <div style={{
                            width: '24px',
                            height: '40px', // Match input height
                            flexShrink: 0
                          }}>
                            {/* Invisible spacer to align with the numbered badge above */}
                          </div>
                          <input
                            className={styles.input}
                            type="url"
                            placeholder="https://example.com/article.pdf"
                            value={urlItem.url}
                            onChange={(e) => updateUrl(urlItem.id, 'url', e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                              }
                              // Prevent any key from bubbling up and potentially closing the modal
                              e.stopPropagation();
                            }}
                            style={{ 
                              flex: 1
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <ActionButton
                    type="button"
                    className="secondary"
                    size="sm"
                    onClick={addUrl}
                    style={{ 
                      marginTop: '0.5rem',
                      padding: '0.75rem 1rem'
                    }}
                  >
                    <FiPlus size={16} />
                    Add URL
                  </ActionButton>
                </div>
                <small className={styles.helperText}>
                  Add multiple reading URLs with custom labels for better organization
                </small>
                </div>
              </div>

              {/* Document File Section */}
              <div className={styles.sectionCard}>
                <div className={styles.sectionHeader}>
                  <span className={styles.sectionHeaderText}>Document File</span>
                </div>
                <FileUploadField
                  id="readings-file-input"
                  label="Document File"
                  optional={true}
                  accept=".pdf,.html,.htm,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                  helperText="Accepted formats: PDF, HTML, DOC, DOCX, XLS, XLSX, PPT, PPTX"
                  value={formData.file}
                  onChange={(file) => setFormData(prev => ({ ...prev, file }))}
                  onClear={() => setFormData(prev => ({ ...prev, file: null }))}
                  existingFileName={getExistingFileName()}
                  existingFileUrl={getExistingFileUrl()}
                />
              </div>

              {/* Week Section */}
              <div className={styles.sectionCard}>
                <div className={styles.sectionHeader}>
                  <span className={styles.sectionHeaderText}>Week</span>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Week (Optional)</label>
                  <input
                    className={styles.input}
                    name="week"
                    type="number"
                    min="1"
                    max="52"
                    value={formData.week}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </>
          )}

          {activeTab === 'syllabus' && (
            <>
              {/* Document File Section */}
              <div className={styles.sectionCard}>
                <div className={styles.sectionHeader}>
                  <span className={styles.sectionHeaderText}>Document File</span>
                </div>
                <FileUploadField
                  id="syllabus-file-input"
                  label="Document File"
                  optional={true}
                  accept=".pdf,.html,.htm,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                  helperText="Accepted formats: PDF, HTML, DOC, DOCX, XLS, XLSX, PPT, PPTX"
                  value={formData.file}
                  onChange={(file) => setFormData(prev => ({ ...prev, file }))}
                  onClear={() => setFormData(prev => ({ ...prev, file: null }))}
                  existingFileName={getExistingFileName()}
                  existingFileUrl={getExistingFileUrl()}
                />
              </div>
            </>
          )}

          {activeTab === 'announcements' && (
            <>
              {/* Priority Section */}
              <div className={styles.sectionCard}>
                <div className={styles.sectionHeader}>
                  <span className={styles.sectionHeaderText}>Priority</span>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Priority</label>
                  <select
                    className={styles.input}
                    name="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>
            </>
          )}

            {/* Inline Footer Actions */}
            <div className={styles.inlineFooter}>
              <div className={styles.footerButtons}>
                {editingContent ? (
                  <>
                    <button 
                      type="button" 
                      className={`${styles.button} ${styles.buttonSecondary}`}
                      onClick={resetForm}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className={`${styles.button} ${styles.buttonPrimary}`}
                      disabled={loading}
                    >
                      {loading ? 'Updating...' : `Update ${getSingularTabName(activeTab)}`}
                    </button>
                  </>
                ) : (
                  <>
                    {onClose && (
                      <button 
                        type="button" 
                        className={`${styles.button} ${styles.buttonSecondary}`}
                        onClick={onClose}
                      >
                        Cancel
                      </button>
                    )}
                    <button 
                      type="submit" 
                      className={`${styles.button} ${styles.buttonPrimary}`}
                      disabled={loading}
                    >
                      {loading ? 'Adding...' : `Add ${getSingularTabName(activeTab)}`}
                    </button>
                  </>
                )}
              </div>
            </div>
          </form>
        )}

        {/* Notifications tab - separate form */}
        {activeTab === 'notifications' && (
          <form onSubmit={handleNotificationsSubmit}>
            <div className={styles.sectionCard}>
              <div className={styles.sectionHeader}>
                <span className={styles.sectionHeaderText}>Email Notifications</span>
              </div>
              <div className={styles.formGroup}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '1rem',
                  padding: '1rem',
                  backgroundColor: 'white',
                  borderRadius: '10px',
                  border: '1px solid rgba(15, 23, 42, 0.1)'
                }}>
                <input
                  type="checkbox"
                  id="sendEmail"
                  name="sendEmail"
                  checked={formData.sendEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, sendEmail: e.target.checked }))}
                  style={{ 
                    width: '18px', 
                    height: '18px',
                    cursor: 'pointer'
                  }}
                />
                <div>
                  <label htmlFor="sendEmail" style={{ 
                    cursor: 'pointer',
                    fontWeight: '500',
                    color: 'var(--text-primary)'
                  }}>
                    Send email notifications to students
                  </label>
                  <p style={{ 
                    margin: '0.5rem 0 0 0',
                    fontSize: '0.9rem',
                    color: 'var(--text-muted)'
                  }}>
                    When enabled, students will receive email notifications when new course content is added.
                    <br />
                    <strong>Note:</strong> This setting applies to all content types and is available to admin users.
                    <br />
                    <strong>Current Status:</strong> 
                    <span style={{ 
                      color: formData.sendEmail ? 'var(--success-color)' : 'var(--error-color)',
                      fontWeight: 'bold',
                      marginLeft: '0.5rem'
                    }}>
                      {formData.sendEmail ? 'ENABLED' : 'DISABLED'}
                    </span>
                  </p>
                </div>
              </div>
              </div>
            </div>

            <div className={styles.inlineFooter}>
              <div className={styles.footerButtons}>
                <button 
                  type="button" 
                  className={`${styles.button} ${styles.buttonSecondary}`}
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className={`${styles.button} ${styles.buttonPrimary}`}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>
          </form>
        )}
      </>
    );
  };

  // Navigation items configuration
  const navItems = [
    { id: 'announcements', label: 'Announcements', icon: FiAlertCircle },
    { id: 'syllabus', label: 'Syllabus', icon: FiBookOpen },
    { id: 'slides', label: 'Slides', icon: FiFileText },
    { id: 'readings', label: 'Readings', icon: FiBookOpen },
    { id: 'recordings', label: 'Recordings', icon: FiVolume2 },
    { id: 'notifications', label: 'Notifications', icon: FiMail },
  ];

  const getCurrentSubTab = () => {
    switch (activeTab) {
      case 'recordings': return recordingsSubTab;
      case 'announcements': return announcementsSubTab;
      case 'syllabus': return syllabusSubTab;
      case 'slides': return slidesSubTab;
      case 'readings': return readingsSubTab;
      default: return 'new';
    }
  };

  const getSetSubTab = (value) => {
    switch (activeTab) {
      case 'recordings': return () => setRecordingsSubTab(value);
      case 'announcements': return () => setAnnouncementsSubTab(value);
      case 'syllabus': return () => setSyllabusSubTab(value);
      case 'slides': return () => setSlidesSubTab(value);
      case 'readings': return () => setReadingsSubTab(value);
      default: return () => {};
    }
  };

  const currentSubTab = getCurrentSubTab();
  const isInstructor = currentUser?.role === 'instructor' || currentUser?.role === 'admin';
  const recordingExternalLinks = activeTab === 'recordings'
    ? (content.recordings || [])
        .map(item => {
          const url = typeof item.url === 'string' ? item.url.trim() : '';
          const week = item?.week;
          return { week, url };
        })
        .filter(item => Boolean(item.url))
    : [];

  const handleCopyDirectVideoLinks = async () => {
    const seen = new Set();
    const formattedLines = [];

    for (const item of recordingExternalLinks) {
      const normalizedWeek = item.week !== undefined && item.week !== null && `${item.week}`.trim() !== ''
        ? `${item.week}`.trim()
        : 'N/A';
      const line = `Week ${normalizedWeek} - ${item.url}`;
      if (!seen.has(line)) {
        seen.add(line);
        formattedLines.push(line);
      }
    }

    if (formattedLines.length === 0) return;

    await copyTextToClipboard(formattedLines.join('\n'), 'All recording links copied to clipboard.');
  };

  return (
    <>
    <div className={styles.pageContainer}>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <h1 className={styles.pageTitle}>
            {editingContent ? `Edit ${getSingularTabName(activeTab)}` : elective ? 'Manage Elective Content' : 'Manage Course Content'}
          </h1>
          <p className={styles.pageSubtext}>
            Add or update course materials.
          </p>
        </div>
        <div className={styles.headerActions}>
          {onClose && (
            <button className={styles.backButton} onClick={onClose}>
              <FiArrowLeft size={16} />
              Back
            </button>
          )}
        </div>
      </div>

      {/* Two-Column Layout */}
      <div className={styles.layoutContainer}>
        {/* Sidebar Navigation */}
        <nav className={styles.sidebarNav} aria-label="Content sections">
          <ul className={styles.navList}>
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.id} className={styles.navItem}>
                  <button
                    className={`${styles.navButton} ${activeTab === item.id ? styles.active : ''}`}
                    onClick={() => handleTabChange(item.id)}
                    aria-current={activeTab === item.id ? 'page' : undefined}
                  >
                    <Icon size={18} />
                    <span>{item.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Mobile Dropdown Navigation */}
        <select
          className={styles.mobileNavSelect}
          value={activeTab}
          onChange={(e) => handleTabChange(e.target.value)}
          aria-label="Select content section"
        >
          {navItems.map((item) => (
            <option key={item.id} value={item.id}>
              {item.label}
            </option>
          ))}
        </select>

        {/* Main Content Area */}
        <div className={styles.mainContent}>
          <div className={styles.contentCard}>
            {/* Sub-tabs - Segmented Control */}
            {activeTab !== 'notifications' && (
              <div className={styles.subTabHeaderRow}>
                <div className={styles.segmentedControl}>
                  <button 
                    className={`${styles.segmentedButton} ${currentSubTab === 'new' ? styles.active : ''}`}
                    onClick={getSetSubTab('new')}
                  >
                    New
                  </button>
                  <button 
                    className={`${styles.segmentedButton} ${currentSubTab === 'existing' ? styles.active : ''}`}
                    onClick={getSetSubTab('existing')}
                  >
                    Existing
                  </button>
                </div>
                {activeTab === 'recordings' && currentSubTab === 'existing' && isInstructor && recordingExternalLinks.length > 0 && (
                  <button
                    type="button"
                    className={styles.copyLinksButton}
                    onClick={handleCopyDirectVideoLinks}
                  >
                    <FiCopy size={14} />
                    <span>Copy All Recording Links</span>
                  </button>
                )}
              </div>
            )}

            {/* Form Content */}
            <div className={styles.formContent}>
              {renderForm()}
            </div>
          </div>
        </div>
      </div>
    </div>
    <Notification
      isOpen={notification.isOpen}
      onClose={() => setNotification(prev => ({ ...prev, isOpen: false }))}
      type={notification.type}
      title={notification.title}
      message={notification.message}
    />
    </>
  );
}

export default AddContentModal;
